// PaymentContext.js
import React, { createContext, useContext, useState } from 'react';
import Config from '../Config';
import { useGlobalContext } from './GlobalContext';

const PaymentContext = createContext(null);

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};

export const PaymentProvider = ({ children }) => {
  const { store, goToStep } = useGlobalContext();
  const [order, setOrder] = useState({});
  const [loading, setLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentHtml, setPaymentHtml] = useState(null);
  const [pspType, setPspType] = useState('redirect');

  const createOrder = async (userData) => {
    setLoading(true);
    setPaymentError(null);

    try {
      const response = await fetch(`${Config.api.base_url}/api/card/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          api_key: Config.api.key,
          store: userData.store,
          cards: userData.cards,
          lng: userData.lng,
          amex: userData.useAmex ? userData.useAmex : null,
          'client': (store.professionalUser?.firstname && store.professionalUser?.lastname) ? store.professionalUser : null,
          proIdentification: userData.proIdentification ? userData.proIdentification : null,
          mobile: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? '1' : '0',
          extraOptins: userData.extraOptins || [],
          discountApplied:userData.discountApplied,
          promotional_code: userData.promotional_code || null,
          clientDeviceInfo: userData.clientDeviceInfo || {}
        })
      });

      const data = await response.json();

      if (data.status === 'ok') {
        setOrder({
          ref: data.result.reference,
          amount: data.result.amount,
          SHASIGN: data.result.SHASIGN
        });

        sessionStorage.setItem('orderData', JSON.stringify({
          step: 'payment',
          order: {
            ref: data.result.reference,
            amount: data.result.amount,
            SHASIGN: data.result.SHASIGN
          }
        }));
        localStorage.setItem('orderData_backup', data.result.reference);

        return data;
      } else {
        if (data.errors[0].code === 2039) {
          
          throw new Error(
            "L’identifiant de société n’est pas celui communiqué lors de votre inscription."
          );
        }
        throw new Error(data.errors[0].message);
      }
    } catch (error) {
      setPaymentError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = (data, paymentHtmlInfo) => {

    setPspType(store.PSP_TYPE);

    if (store.PSP_TYPE === 'redirect') {
      const paymentForm = document.getElementById('paymentForm');
      let url = store.PSP_URL;
      const method = store.PSP_METHOD;

      // Remplir les champs du formulaire
      Object.entries(store.PSP_fields || {}).forEach(([key, value]) => {
        if (data[key] !== undefined) {
          const input = document.getElementById(key);
          if (input) input.value = data[key];
        } else {
          const input = document.getElementById(key);
          if (input) input.remove();
        }
      });

      if (data.url) {
        url = data.url;
      }

      if (method === 'get') {
        window.location.href = url;
      } else {
        paymentForm.setAttribute('action', url);
        paymentForm.submit();
      }
    }
    else if (store.PSP_TYPE === 'local-form') {
      goToStep('payment');
      setPaymentHtml(paymentHtmlInfo);
    }
    else if (store.PSP_TYPE === 'overlay') {
      if (paymentHtmlInfo.js) {
        const script = document.createElement('script');
        script.src = paymentHtmlInfo.js;
        script.type = 'text/javascript';
        document.head.appendChild(script);
      }

      const paymentForm = document.getElementById('paymentForm');
      if (paymentForm) {
        Object.entries(store.PSP_fields || {}).forEach(([key, value]) => {
          if (data[key] !== undefined) {
            const input = document.getElementById(key);
            if (input) input.value = data[key];
          } else {
            const input = document.getElementById(key);
            if (input) input.remove();
          }
        });
      }

      if (paymentHtmlInfo.inlinejs) {
        setTimeout(() => {
          try {
            eval(paymentHtmlInfo.inlinejs);
          } catch (error) {
            console.error('Error executing overlay script:', error);
          }
        }, 800);
      }
    }
  };
  const getPollingDelay = (attempts) => {
    const elapsedSeconds = attempts * 5 <= 60 ? attempts * 5 : 60 + (attempts - 12) * 7;
    if (elapsedSeconds < 60) return 5000;
    if (elapsedSeconds < 120) return 7000;
    return 10000;
  };

  const checkTransaction = async (reference, status, params, attempts = 0) => {
    try {
      const storedOrderData = sessionStorage.getItem('orderData');
      const backupRef = localStorage.getItem('orderData_backup');
      const storedOrder = storedOrderData ? JSON.parse(storedOrderData).order : null;

      const orderRef = reference || (storedOrder ? storedOrder.ref : backupRef);
      if (!orderRef) {
        return {
          status: 'exception',
          errors: [{ message: 'Aucune référence de commande trouvée' }]
        };
      }
      const response = await fetch(`${Config.api.base_url}/api/card/confirm-command`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          api_key: Config.api.key,
          reference: orderRef,
          status,
          clientPro: null, 
          psp_params: params || {}
        })
      });
  
      const data = await response.json();
  
      if (data.status === 'ok') {
        if (orderRef && orderRef === data.result.ref) {
          if (!data.result.cards || data.result.cards.length === 0) {
            await new Promise(resolve => setTimeout(resolve, getPollingDelay(attempts)));
            return checkTransaction(orderRef, status, params, attempts + 1);
          }

          const firstCard = data.result.cards[0];

          if ((status === 'result' && firstCard.status !== 'unpayed') ||
              (status !== 'result' && (status !== "authorized" || firstCard.idTransaction != null))) {
            localStorage.removeItem("orderData_backup")
            sessionStorage.removeItem('totalAmounts');
            sessionStorage.removeItem('cart');
            sessionStorage.removeItem('promoCode');
            sessionStorage.removeItem('promoDiscount');

            return {
              ...data.result,
              status: status === "result" ? firstCard.status : status,
              store: firstCard.physical_cart_group ? 'physical' : 'digital'
            };
          }
          else if ((status === 'result' && firstCard.status === 'unpayed') ||
                   (status !== 'result' && status === "authorized")) {

            await new Promise(resolve => setTimeout(resolve, getPollingDelay(attempts)));
            return checkTransaction(orderRef, status, params, attempts + 1);
          }
        } else {
          localStorage.removeItem("orderData_backup")
          sessionStorage.removeItem('totalAmounts');
          sessionStorage.removeItem('cart');
          return {
            status: 'exception',
            errors: [{ message: 'Référence de commande invalide' }]
          };
        }
      } else {
        return {
          status: 'exception',
          errors: data.errors
        };
      }
  
      return data;
  
    } catch (error) {
      localStorage.removeItem("orderData_backup")
      return {
        status: 'exception',
        errors: [{ message: error.message }]
      };
    }
  };


  return (
    <PaymentContext.Provider value={{
      order,
      loading,
      paymentError,
      handlePayment,
      createOrder,
      paymentHtml,
      checkTransaction,
      pspType
    }}>
      {children}
    </PaymentContext.Provider>
  );
};
